import React, { useState } from "react";
import { ChevronsUpDown, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

interface Option {
  value: string;
  label: string;
  description?: string;
}

interface MultiSelectProps {
  options?: Option[];
  placeholder?: string;
  emptyMessage?: string;
  maxDisplay?: number;
  value?: string[];
  onValueChange?: (value: string[]) => void;
  allowCustom?: boolean;
  customLabel?: string;
  disabled?: boolean;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options = [],
  placeholder = "Select items...",
  emptyMessage = "No items found.",
  maxDisplay = 3,
  value,
  onValueChange,
  allowCustom = false,
  customLabel = "Create",
  disabled = false,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [internalValue, setInternalValue] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");

  // Use controlled or uncontrolled state
  const isControlled = value !== undefined;
  const selectedValues = isControlled ? value : internalValue;
  const setSelectedValues = isControlled ? onValueChange! : setInternalValue;

  const handleSelect = (optionValue: string) => {
    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((item) => item !== optionValue)
      : [...selectedValues, optionValue];

    setSelectedValues(newValues);
  };

  const handleCreateCustom = () => {
    if (searchValue.trim() && !selectedValues.includes(searchValue.trim())) {
      const newValues = [...selectedValues, searchValue.trim()];
      setSelectedValues(newValues);
      setSearchValue("");
    }
  };

  const handleRemove = (optionValue: string, event?: React.MouseEvent) => {
    // Prevent the click from bubbling up to the PopoverTrigger button
    if (event) {
      event.stopPropagation();
    }
    const newValues = selectedValues.filter((item) => item !== optionValue);
    setSelectedValues(newValues);
  };

  const clearAll = () => {
    setSelectedValues([]);
  };

  // Create a map of all items (predefined + custom)
  const allItemsMap = new Map<string, string>();
  options.forEach(opt => allItemsMap.set(opt.value, opt.label));
  selectedValues.forEach(val => {
    if (!allItemsMap.has(val)) {
      allItemsMap.set(val, val); // Custom tags use value as label
    }
  });

  const selectedItems = selectedValues.map(val => ({
    value: val,
    label: allItemsMap.get(val) ?? val
  }));
  const displayItems = selectedItems.slice(0, maxDisplay);
  const remainingCount = selectedItems.length - maxDisplay;

  // Check if search value matches any option or is a new custom tag
  const searchLower = searchValue.toLowerCase();
  const hasExactMatch = options.some(opt => opt.value.toLowerCase() === searchLower);
  const canCreateCustom = allowCustom && searchValue.trim() && !hasExactMatch && !selectedValues.includes(searchValue.trim());

  return (
    <div className="w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "w-full flex items-center justify-between min-h-10 h-auto py-2 px-3 text-sm",
              "border rounded-md cursor-pointer",
              "bg-white dark:bg-black dark:border-input",
              "hover:bg-accent hover:text-accent-foreground",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-transparent",
            )}
            onClick={() => !disabled && setOpen(!open)}
          >
            <div className="flex flex-wrap items-center gap-1 flex-1 min-w-0 pr-2">
              {selectedValues.length === 0 && (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
              {selectedValues.length > 0 && displayItems.map((item) => (
                <span key={item.value}>
                  <Badge variant="secondary" onClose={(e) => handleRemove(item.value, e)}>
                    {item.label}
                  </Badge>
                </span>
              ))}
              {selectedValues.length > 0 && remainingCount > 0 && <Badge variant="outline">+{remainingCount} more</Badge>}
            </div>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput placeholder="Search items..." value={searchValue} onValueChange={setSearchValue} />
            <CommandList>
              <CommandEmpty>
                {canCreateCustom ? (
                  <div className="py-6 text-center text-sm">
                    <p className="text-muted-foreground mb-2">No matching items found.</p>
                    <button
                      onClick={handleCreateCustom}
                      className="inline-flex items-center gap-2 text-primary hover:underline"
                    >
                      <Plus className="h-4 w-4" />
                      {customLabel} "{searchValue}"
                    </button>
                  </div>
                ) : (
                  emptyMessage
                )}
              </CommandEmpty>
              <CommandGroup>
                {canCreateCustom && (
                  <CommandItem onSelect={handleCreateCustom} className="text-primary">
                    <Plus className="mr-2 h-4 w-4" />
                    {customLabel} "{searchValue}"
                  </CommandItem>
                )}
                {selectedValues.length > 0 && (
                  <CommandItem onSelect={clearAll} className="justify-center text-center">
                    Clear all ({selectedValues.length})
                  </CommandItem>
                )}
                {options.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <CommandItem key={option.value} value={option.label} onSelect={() => handleSelect(option.value)}>
                      <Checkbox checked={isSelected} className="mr-2" />
                      <div className="flex flex-col">
                        <span>{option.label}</span>
                        {option.description && (
                          <span className="text-xs text-muted-foreground">{option.description}</span>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
